/**
 * ForgotPasswordPage Component
 *
 * First step of password reset flow. User enters email to request password reset link.
 * Backend sends reset link to user's email with a token.
 *
 * Flow:
 * 1. User enters email address
 * 2. Backend sends password reset email with link containing token
 * 3. User clicks link in email (includes token)
 * 4. Redirected to ResetPasswordPage with token in URL
 * 5. User enters new password and confirms
 * 6. Redirected to login page
 *
 * Usage:
 *   <Route path="/forgot-password" element={<ForgotPasswordPage />} />
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthShell, AuthTitle, AuthField, AuthSubmit, AuthNote, AuthGhost, authLink } from '@/components/auth/AuthShell';
import { apiClient } from '@/services/api';

type ForgotPasswordState = 'form' | 'loading' | 'success' | 'error';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<ForgotPasswordState>('form');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate email
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setState('loading');

      // Call backend to request password reset
      await apiClient.post('/api/v1/auth/forgot-password', {
        email: email.toLowerCase(),
      });

      // Success - show confirmation message
      setState('success');
      setLoading(false);

      // Redirect to login after 5 seconds
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Password reset link has been sent to your email. Please check your inbox.',
          },
          replace: true,
        });
      }, 5000);
    } catch (err: any) {
      setLoading(false);
      setState('error');

      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to process password reset request';

      setError(errorMessage);
    }
  };

  return (
    <AuthShell headline={<>New password, in a minute.</>} sub="We’ll email you a link. It works for one hour.">
      {(state === 'form' || state === 'loading') && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <AuthTitle sub="Enter the email you sign in with.">Reset password</AuthTitle>
          {error && <AuthNote>{error}</AuthNote>}
          <AuthField label="Email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }}
            placeholder="you@example.com" autoComplete="email" autoFocus disabled={loading} />
          <AuthSubmit loading={loading}>Send the link</AuthSubmit>
          <p className="m-0 text-center text-sm text-[#4A5468]">Remembered it? <Link to="/login" className={authLink}>Sign in</Link></p>
        </form>
      )}
      {state === 'success' && (
        <div className="flex flex-col gap-4">
          <span className="text-label uppercase text-[#14603F]">Sent</span>
          <AuthTitle sub={<>We sent a reset link to <strong className="text-[#0F1526]">{email}</strong>. It works for one hour. Taking you back to sign in…</>}>Check your email</AuthTitle>
          <AuthGhost onClick={() => navigate('/login', { replace: true })}>Back to sign in</AuthGhost>
        </div>
      )}
      {state === 'error' && (
        <div className="flex flex-col gap-4">
          <AuthTitle>That didn’t work</AuthTitle>
          {error && <AuthNote>{error}</AuthNote>}
          <div className="grid grid-cols-2 gap-2">
            <AuthGhost onClick={() => { setState('form'); setError(null); }}>Try again</AuthGhost>
            <AuthSubmit type="button" onClick={() => navigate('/login', { replace: true })}>Sign in</AuthSubmit>
          </div>
        </div>
      )}
    </AuthShell>
  );
};

export default ForgotPasswordPage;
